import {
    faChevronDoubleLeft,
    faChevronDoubleRight,
    faChevronLeft,
    faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { buttonStyle } from 'components/button/Button';
import * as React from 'react';
import { useMemo } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { createRange } from 'utils/array';

export type PagingProps = {
    /**
     * The function to call when the current page number has changed.
     */
    onPageChange: (page: number) => void;
    /**
     * The current (active) page number.
     */
    currentPage: number;
    /**
     * The total number of pages, or the maximum selectable page.
     */
    totalPages: number;
    /**
     * The number of paging buttons to show.
     */
    shownPages?: number;
    /**
     * Classes to apply to the pagination container.
     */
    className?: string;
    /**
     * Classes to apply to the pagination container.
     */
    style?: React.CSSProperties;
};

type PageButtonStyleProps = {
    $active?: boolean;
    width?: string;
    height?: string;
    $activeColor?: string;
    $activeBackground?: string;
    $hoverColor?: string;
    $hoverBackground?: string;
};

const PageButton = styled.button<PageButtonStyleProps>`
    ${buttonStyle}

    display: inline-flex;
    align-items: center;
    justify-content: center;

    color: ${({ $active, $activeColor }) =>
        $active ? ($activeColor ?? tokens.text.onColor) : 'inherit'};
    background-color: ${({ $active, $activeBackground }) =>
        $active ? ($activeBackground ?? tokens.primary.default) : 'transparent'};
    border-radius: 999px;
    width: 3rem;
    height: 3rem;
    border: 1px solid transparent;

    &:not(:disabled) {
        &:hover {
            color: ${({ $hoverColor }) => $hoverColor ?? 'inherit'};
            background-color: ${({ $hoverBackground }) =>
                $hoverBackground ?? tokens.action.hover};
            border: 1px solid ${tokens.border.strong};
        }
    }
`;

/**
 * The default number of page options to show inside group of pages.
 */
const defaultShownPages = 5;

/**
 * Shows a pagination component which lets users select pages between 1 and the
 * total number of pages given. Supports common functionality like: next page,
 * previous page, go to first page, go to last page, and page grouping.
 */
export function Paging({
    totalPages,
    onPageChange,
    currentPage,
    shownPages = defaultShownPages,
    className,
    style,
}: PagingProps) {
    const pageGroupStart = Math.floor((currentPage - 1) / shownPages) * shownPages + 1;
    const pageGroupEnd = Math.min(totalPages, pageGroupStart + shownPages - 1);
    const pageNumbers = createRange(pageGroupStart, pageGroupEnd);

    const {
        nextPage,
        previousPage,
        nextPageGroup,
        previousPageGroup,
        goToFirstPage,
        goToLastPage,
    } = useMemo(
        () => ({
            nextPage: () => onPageChange(currentPage + 1),
            previousPage: () => onPageChange(currentPage - 1),
            nextPageGroup: () => onPageChange(pageGroupEnd + 1),
            previousPageGroup: () => onPageChange(pageGroupStart - 1),
            goToFirstPage: () => onPageChange(1),
            goToLastPage: () => onPageChange(totalPages),
        }),
        [currentPage, onPageChange, pageGroupEnd, pageGroupStart, totalPages],
    );

    const hasNextPage = currentPage < totalPages;
    const hasPreviousPage = currentPage > 1;
    const hasNextPageGroup = pageGroupStart + shownPages < totalPages;
    const hasPreviousPageGroup = pageGroupStart > 1;
    const hasMorePages = totalPages > shownPages;

    return (
        <nav className={`f-14 ${className}`} aria-label="pagination" style={style}>
            {hasMorePages && (
                <>
                    <PageButton
                        onClick={goToFirstPage}
                        disabled={!hasPreviousPage}
                        aria-label="Go to first page"
                        $hoverColor={tokens.text.onColor}
                        $hoverBackground={tokens.primary.default}
                    >
                        <FontAwesomeIcon icon={faChevronDoubleLeft} />
                    </PageButton>
                    <PageButton
                        onClick={previousPage}
                        disabled={!hasPreviousPage}
                        aria-label="Go to previous page"
                        $hoverColor={tokens.text.onColor}
                        $hoverBackground={tokens.primary.default}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </PageButton>
                    {hasPreviousPageGroup && (
                        <PageButton
                            onClick={previousPageGroup}
                            aria-label="Go to previous page group"
                        >
                            &hellip;
                        </PageButton>
                    )}
                </>
            )}
            {pageNumbers.map((pageNumber) => (
                <PageButton
                    key={pageNumber}
                    $active={pageNumber === currentPage}
                    onClick={() => onPageChange(pageNumber)}
                    aria-current={pageNumber === currentPage ? 'page' : undefined}
                    aria-label={`Go to page ${pageNumber}`}
                >
                    {pageNumber}
                </PageButton>
            ))}
            {hasMorePages && (
                <>
                    {hasNextPageGroup && (
                        <PageButton
                            onClick={nextPageGroup}
                            aria-label="Go to next page group"
                        >
                            &hellip;
                        </PageButton>
                    )}
                    <PageButton
                        onClick={nextPage}
                        disabled={!hasNextPage}
                        aria-label="Go to next page"
                        $hoverColor={tokens.text.onColor}
                        $hoverBackground={tokens.primary.default}
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </PageButton>
                    <PageButton
                        onClick={goToLastPage}
                        disabled={!hasNextPageGroup}
                        aria-label="Go to last page"
                        $hoverColor={tokens.text.onColor}
                        $hoverBackground={tokens.primary.default}
                    >
                        <FontAwesomeIcon icon={faChevronDoubleRight} />
                    </PageButton>
                </>
            )}
        </nav>
    );
}
